import axios, { AxiosError, type AxiosRequestConfig, type AxiosInstance } from 'axios'
import { storage } from './platform/storage'
import type {
  Conversation,
  GroupDetail,
  GroupMember,
  Message,
  Pagination,
  UserSearchResult,
} from './types'
import type { Note, NotePagination, CreateNotePayload, UpdateNotePayload } from './notes.types'
import type {
  Plan,
  Product,
  ProductKey,
  BillingStatus,
  BillingUsage,
  Payment,
} from './billing.types'

/**
 * Порт mobile/lib/apiClient.ts.
 *
 * Отличия от мобильной версии, все намеренные:
 *  - базовый URL относительный (/api): nginx отдаёт статику и проксирует API
 *    с того же origin, поэтому CORS не участвует;
 *  - refresh-токен живёт в httpOnly-куке, а не в хранилище — при XSS его
 *    нельзя выкрасть. В localStorage он кладётся только когда явно разрешено
 *    для разработки против удалённого бэкенда по http;
 *  - при окончательном провале refresh поднимается событие, иначе стор
 *    оставался с isAuthenticated: true при уже стёртых токенах.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'
const REFRESH_TOKEN_KEY = 'lightchat_refresh_token'

/**
 * Разрешает хранить refresh-токен в localStorage. Нужно только для разработки
 * против прод-бэкенда по http: там кука выставлена с secure=true и браузер
 * её не отдаёт. В прод-сборке всегда false.
 */
const ALLOW_TOKEN_STORAGE =
  import.meta.env.DEV && import.meta.env.VITE_ALLOW_TOKEN_STORAGE === 'true'

// Access-токен только в памяти
let accessTokenInMemory: string | null = null
let isRefreshing = false
let refreshPromise: Promise<string | null> | null = null

/** Поднимается, когда обновить сессию не удалось и пользователя надо разлогинить. */
export const AUTH_FAILURE_EVENT = 'lightchat:auth-failure'

/**
 * Проверить сессию не удалось — и это НЕ то же самое, что «сессии нет».
 *
 * Различать обязательно. Установленное приложение запускается офлайн,
 * /auth/refresh падает на сети — и без такого разделения провал сети
 * читался как «сессия кончилась», человека выбрасывало на экран логина,
 * где он офлайн ничего сделать не может. Библия при этом лежит в кэше
 * и прекрасно читается.
 *
 * Сюда же относится 5xx: упавший на минуту бэкенд не повод разлогинить
 * всех, кто в этот момент открыл приложение. «Сессии нет» говорит только
 * сам сервер ответом 401 или 403.
 */
export class SessionUnknownError extends Error {
  constructor(reason: string) {
    super(`Проверить сессию не удалось: ${reason}`)
    this.name = 'SessionUnknownError'
  }
}

/**
 * Сбой, по которому о сессии ничего не известно: ответа не было вовсе
 * либо сервер ответил своей ошибкой.
 */
export function isTransientFailure(error: unknown): boolean {
  if (error instanceof SessionUnknownError) return true
  const axiosError = error as AxiosError | undefined
  if (!axiosError?.isAxiosError) return false
  const status = axiosError.response?.status
  if (status === undefined) return true
  return status >= 500 || status === 408 || status === 429
}

function emitAuthFailure(): void {
  window.dispatchEvent(new CustomEvent(AUTH_FAILURE_EVENT))
}

function getStoredRefreshToken(): string | null {
  return ALLOW_TOKEN_STORAGE ? storage.get(REFRESH_TOKEN_KEY) : null
}

export function setTokens({
  accessToken,
  refreshToken,
}: {
  accessToken: string
  refreshToken?: string | undefined
}): void {
  if (accessToken) accessTokenInMemory = accessToken
  if (refreshToken && ALLOW_TOKEN_STORAGE) {
    storage.set(REFRESH_TOKEN_KEY, refreshToken)
  }
}

export function clearTokens(): void {
  accessTokenInMemory = null
  storage.remove(REFRESH_TOKEN_KEY)
}

export function getAccessToken(): string | null {
  return accessTokenInMemory
}

function extractTokensFromResponse(raw: unknown): {
  accessToken?: string
  refreshToken?: string
} {
  if (!raw || typeof raw !== 'object') return {}

  type TokenPair = { accessToken?: string; refreshToken?: string }
  const body = raw as { data?: TokenPair & { tokens?: TokenPair } } & TokenPair

  if (body.data?.tokens) return body.data.tokens
  if (body.data?.accessToken || body.data?.refreshToken) return body.data
  if (body.accessToken || body.refreshToken) return body
  return {}
}

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  // Обязательно: refresh-кука httpOnly и уходит только с этим флагом
  withCredentials: true,
})

api.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Отдельный instance без перехватчиков — только для /auth/refresh,
// иначе 401 от самого refresh уводит в бесконечную рекурсию.
const plainAxios = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
})

async function refreshTokenOnce(): Promise<string | null> {
  const stored = getStoredRefreshToken()

  let res
  try {
    // Тело нужно только в режиме хранения токена; в норме сервер берёт куку.
    res = await plainAxios.post('/auth/refresh', stored ? { refreshToken: stored } : {})
  } catch (error) {
    // Ответа не было или сервер отдал свою ошибку — про сессию это
    // не говорит ничего
    if (isTransientFailure(error)) {
      const status = (error as AxiosError).response?.status
      throw new SessionUnknownError(status ? `сервер ответил ${status}` : 'нет связи с сервером')
    }
    throw error
  }

  const { accessToken, refreshToken } = extractTokensFromResponse(res.data)
  if (accessToken) {
    setTokens({ accessToken, refreshToken })
  }
  return accessToken ?? null
}

/**
 * Обновление сессии, разделяемое всеми параллельными запросами.
 * Без этого десяток одновременных 401 запустил бы десяток ротаций
 * refresh-токена, и все, кроме одной, отозвали бы друг друга.
 */
export function refreshSession(): Promise<string | null> {
  if (!isRefreshing) {
    isRefreshing = true
    refreshPromise = refreshTokenOnce()
      .catch((error: unknown) => {
        // 401/403 означают «сессии нет» и возвращаются как null.
        // Всё остальное означает «неизвестно» и пробрасывается дальше:
        // вызывающий сам решит, разлогинивать или подождать.
        if (error instanceof SessionUnknownError) throw error
        return null
      })
      .finally(() => {
        isRefreshing = false
        refreshPromise = null
      })
  }
  return refreshPromise as Promise<string | null>
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined
    const status = error.response?.status

    if (status === 401 && original && !original._retry) {
      original._retry = true

      let newAccess: string | null = null
      try {
        newAccess = await refreshSession()
      } catch {
        // Связь пропала между 401 и попыткой обновиться либо сервер лёг.
        // Сессию не трогаем: выбрасывать из приложения из-за сети — то же,
        // что разлогинивать человека в метро.
        return Promise.reject(error)
      }

      if (newAccess) {
        original.headers = original.headers ?? {}
        ;(original.headers as Record<string, string>).Authorization = `Bearer ${newAccess}`
        return api(original)
      }

      clearTokens()
      emitAuthFailure()
    }

    return Promise.reject(error)
  },
)

type Envelope<T> = { success: boolean; data: T }

export const notesApi = {
  list(limit = 20, offset = 0) {
    return api.get<Envelope<{ notes: Note[]; pagination: NotePagination }>>('/notes', {
      params: { limit, offset },
    })
  },
  getById(id: string) {
    return api.get<Envelope<{ note: Note }>>(`/notes/${id}`)
  },
  create(payload: CreateNotePayload) {
    return api.post<Envelope<{ note: Note }>>('/notes', payload)
  },
  update(id: string, payload: UpdateNotePayload) {
    return api.put<Envelope<{ note: Note }>>(`/notes/${id}`, payload)
  },
  delete(id: string) {
    return api.delete(`/notes/${id}`)
  },
}

export type CreateConversationPayload = {
  context?:
    | {
        type: 'bible_verses' | 'note' | 'plan_day'
        data: Record<string, unknown>
      }
    | undefined
}

export const conversationsApi = {
  list(limit = 20, offset = 0) {
    return api.get<Envelope<{ conversations: Conversation[]; pagination: Pagination }>>(
      '/conversations',
      { params: { limit, offset } },
    )
  },
  getById(id: string, messageLimit = 50) {
    return api.get<
      Envelope<{ conversation: Conversation; messages: Message[]; pagination: Pagination }>
    >(`/conversations/${id}`, { params: { messageLimit } })
  },
  create(payload?: CreateConversationPayload) {
    return api.post<Envelope<{ conversation: Conversation }>>('/conversations', payload ?? {})
  },
  update(id: string, payload: { title: string }) {
    return api.patch<Envelope<{ conversation: Conversation }>>(`/conversations/${id}`, payload)
  },
  delete(id: string) {
    return api.delete(`/conversations/${id}`)
  },
  getMessages(conversationId: string, limit = 50, offset = 0) {
    return api.get<Envelope<{ messages: Message[]; pagination: Pagination }>>(
      `/conversations/${conversationId}/messages`,
      { params: { limit, offset } },
    )
  },
}

export const billingApi = {
  plans() {
    return api.get<Envelope<Plan[]>>('/billing/plans')
  },
  status() {
    return api.get<Envelope<BillingStatus>>('/billing/status')
  },
  usage() {
    return api.get<Envelope<BillingUsage>>('/billing/usage')
  },
  products() {
    return api.get<Envelope<Product[]>>('/billing/products')
  },
  /** Создаёт платёж и возвращает ссылку на страницу оплаты ЮKassa. */
  purchase(productKey: ProductKey) {
    return api.post<
      Envelope<{
        paymentId: string
        redirectUrl: string
        amount: number
        plan: 'pro' | 'max' | 'pack'
        product: ProductKey
      }>
    >('/billing/purchase', { productKey })
  },
  payments() {
    return api.get<Envelope<{ payments: Payment[] }>>('/billing/payments')
  },
  paymentStatus(id: string) {
    return api.get<Envelope<Payment>>(`/billing/payments/${id}`)
  },
}

export const groupsApi = {
  list(limit = 20, offset = 0) {
    return api.get<
      Envelope<{
        groups: Array<{
          id: string
          name: string
          type: 'group'
          participantCount: number
          lastMessageAt: string | null
          createdAt: string
          updatedAt: string
        }>
        pagination: Pagination
      }>
    >('/groups', { params: { limit, offset } })
  },
  getById(id: string, messageLimit = 50) {
    return api.get<Envelope<GroupDetail>>(`/groups/${id}`, { params: { messageLimit } })
  },
  create(payload: { name: string; memberEmails: string[] }) {
    return api.post<
      Envelope<{
        group: { id: string; name: string; type: 'group'; createdAt: string; updatedAt: string }
        members: GroupMember[]
      }>
    >('/groups', payload)
  },
  updateName(id: string, name: string) {
    return api.patch(`/groups/${id}`, { name })
  },
  addMember(groupId: string, email: string) {
    return api.post<Envelope<{ member: GroupMember }>>(`/groups/${groupId}/members`, { email })
  },
  removeMember(groupId: string, userId: string) {
    return api.delete(`/groups/${groupId}/members/${userId}`)
  },
  leave(groupId: string) {
    return api.post(`/groups/${groupId}/leave`)
  },
  delete(groupId: string) {
    return api.delete(`/groups/${groupId}`)
  },
}

export const usersApi = {
  /** Поиск по точному email — бэкенд больше не отдаёт совпадения по префиксу. */
  searchByEmail(email: string) {
    return api.get<Envelope<{ users: UserSearchResult[] }>>('/users/search', { params: { email } })
  },
}

export const authApi = {
  register(payload: { email: string; password: string; source?: string; sourcePage?: string; referralCode?: string }) {
    return api.post('/auth/register', payload)
  },
  login(payload: { email: string; password: string }) {
    return api.post('/auth/login', payload)
  },
  forgotPassword(payload: { email: string; newPassword: string }) {
    return api.post('/auth/forgot-password', payload)
  },
  verifyCode(payload: { email: string; code: string; type: 'registration' | 'password_reset' }) {
    return api.post('/auth/verify-code', payload)
  },
  me() {
    return api.get('/me')
  },
  updateMe(payload: {
    name: string | null
    sex?: 'male' | 'female' | null
    birthDate?: string | null
  }) {
    return api.patch('/me', payload)
  },
  changePassword(payload: { currentPassword: string; newPassword: string }) {
    return api.patch<{ success: boolean; message: string }>('/me/password', payload)
  },
  /** Удаление аккаунта: подтверждается текущим паролем. */
  deleteMe(password: string) {
    return api.delete<{ success: boolean; message: string }>('/me', { data: { password } })
  },
  logout() {
    const stored = getStoredRefreshToken()
    return api.post('/auth/logout', stored ? { refreshToken: stored } : {})
  },
}

/** Достаёт человекочитаемое сообщение из ответа бэкенда. */
export function apiErrorMessage(error: unknown, fallback = 'Что-то пошло не так'): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string; message?: string } | undefined
    if (!error.response) {
      return 'Нет связи с сервером. Проверьте подключение к интернету.'
    }
    return data?.error || data?.message || fallback
  }
  return (error as Error)?.message || fallback
}

/** Код ошибки бэкенда: MESSAGE_LIMIT_EXCEEDED, PLAN_REQUIRED и т.п. */
export function apiErrorCode(error: unknown): string | null {
  if (axios.isAxiosError(error)) {
    return ((error.response?.data as { code?: string } | undefined)?.code) ?? null
  }
  return null
}
